import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api, { auth as tokenStore } from '../api/axios';

export type Role = 'ADMIN' | 'COMPTABLE' | 'COMMERCIAL' | 'PLATFORM_ADMIN';

export interface AuthUser {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  role: Role;
  actif: boolean;
  defaultCompanyId?: number | null;
}

export interface CompanySummary {
  id: number;
  raisonSociale: string;
  matriculeFiscal: string;
}

interface AuthState {
  user: AuthUser | null;
  companies: CompanySummary[];
  activeCompanyId: number | null;
  loading: boolean;
  login: (email: string, motDePasse: string) => Promise<void>;
  register: (data: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  switchCompany: (companyId: number) => void;
  hasPermission: (code: string) => boolean;
}

export interface RegisterPayload {
  nom: string;
  prenom: string;
  email: string;
  motDePasse: string;
  companyRaisonSociale: string;
  companyMatriculeFiscal: string;
  companyAdresse: string;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [activeCompanyId, setActiveCompanyId] = useState<number | null>(() => {
    const stored = tokenStore.getCompanyId();
    return stored ? Number(stored) : null;
  });
  const [loading, setLoading] = useState<boolean>(!!tokenStore.getAccessToken());
  const [permissions, setPermissions] = useState<string[]>([]);

  const loadPermissions = useCallback(async () => {
    try {
      const resp = await api.get<string[]>('/me/permissions');
      setPermissions(resp.data ?? []);
    } catch { setPermissions([]); }
  }, []);

  const hasPermission = useCallback(
    (code: string) => permissions.length === 0 ? true : permissions.includes(code),
    [permissions],
  );

  const hydrate = useCallback(async () => {
    if (!tokenStore.getAccessToken()) { setLoading(false); return; }
    try {
      const resp = await api.get('/companies');
      setCompanies(resp.data);
      // Decode user from JWT payload (sub + claims) to restore identity after reload.
      const token = tokenStore.getAccessToken();
      if (token) {
        const [, payloadB64] = token.split('.');
        if (payloadB64) {
          const padded = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
          const payload = JSON.parse(atob(padded + '=='.slice(0, (4 - padded.length % 4) % 4)));
          setUser({
            id: Number(payload.sub),
            email: payload.email,
            role: payload.role,
            nom: '', prenom: '', actif: true,
          });
        }
      }
    } catch {
      // Will be redirected by 401 interceptor.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { hydrate().then(loadPermissions); }, [hydrate, loadPermissions]);

  const applyAuth = (data: any) => {
    tokenStore.setTokens(data.accessToken, data.refreshToken);
    tokenStore.setCompanyId(data.activeCompanyId ?? null);
    setUser(data.user);
    setCompanies(data.companies || []);
    setActiveCompanyId(data.activeCompanyId ?? null);
    loadPermissions();
  };

  const login = async (email: string, motDePasse: string) => {
    const resp = await api.post('/auth/login', { email, motDePasse });
    applyAuth(resp.data);
  };

  const register = async (data: RegisterPayload) => {
    const resp = await api.post('/auth/register', data);
    applyAuth(resp.data);
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    tokenStore.clear();
    setUser(null);
    setCompanies([]);
    setActiveCompanyId(null);
  };

  const switchCompany = (companyId: number) => {
    tokenStore.setCompanyId(companyId);
    setActiveCompanyId(companyId);
    // Full reload so every query refetches with the new tenant header.
    window.location.reload();
  };

  return (
    <AuthContext.Provider value={{ user, companies, activeCompanyId, loading, login, register, logout, switchCompany, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthState => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
